/* ============================================================
   Dathent — Plans page ("Choose your plan")
   Ported 1:1 from plans-pack (Claude Design). The canonical, detailed
   plans surface — every Upgrade CTA and the UpgradeModal "See all plans"
   link route here; Settings → Billing's "Change plan" navigates here too
   (the embedded grid was removed from Settings).
   Reuses lib/plans.js (PLAN_DEFS / PLAN_TEXT_KEYS / startCheckout) so the
   live Stripe path is byte-identical and the copy can't drift.
   Scoped CSS in styles/plans-page.css.
   ============================================================ */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { PLAN_DEFS, PLAN_TEXT_KEYS, PLAN_ID_BY_KEY, NEW_PLAN_DEFS, NEW_PLAN_TEXT_KEYS, NEW_PLAN_ID_BY_KEY, startCheckout } from '../lib/plans.js';
import { formatPostingMoney } from '../lib/postingPlan.js';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import ContactOptionsModal from '../components/ContactOptionsModal.jsx';
import ProductVideoLink from '../components/ProductVideoLink.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { buildPlanTracks, gridColsClass, resolveSwitchLabel } from '../lib/planTracks.js';
import { planIncludesResearch, missingBrandFields } from '../lib/activationProgress.js';
import { clearPlanIntent, getPlanIntent } from '../lib/planIntent.js';
import {
  canSwitchPlanInPlace,
  shouldShowCancelSchedulePick,
  resolveScheduledDowngrade,
  resolveSwitchNotice,
  resolveSwitchError,
  resolveGenericSwitchError,
  refetchEntitlementsUntilPlanMatches,
} from '../lib/planSwitchTrial.js';
import { paymentIssueLinks } from '../lib/paymentIssue.js';
import { resolveManagedPaymentsNewSubscriptions } from '../lib/managedPayments.js';
import { resolveSocialAddonState, resolveSocialAddonPricing, resolveIncludedForPlanCard } from '../lib/socialAddon.js';
import SocialAddonProvenanceLine from '../components/billing/SocialAddonProvenanceLine.jsx';
import ConnectedAccountsSheet from '../components/billing/ConnectedAccountsSheet.jsx';
import PlanSwitchReviewDialog from '../components/billing/PlanSwitchReviewDialog.jsx';
import { buildPlanSwitchReview, planSwitchReviewMatches } from '../lib/schedulerSwitchReview.js';
import { isRecognizedBillingState } from '../lib/billingState.js';
import { newAccountTrialCheckoutOptions } from '../lib/planTrial.js';

// Mirror of Settings.jsx resolveCurrentPlanId — kept local so the page is
// self-contained. Maps backend entitlements → a PLAN_DEFS id.
//
// Checked FIRST against NEW_PLAN_ID_BY_KEY (starter/growth/pro/agency — an
// identity map, see lib/plans.js), then falls through to the untouched legacy
// resolution below. Safe to check unconditionally (not just when
// newGridActive): the two key namespaces are disjoint by construction — a
// backend planKey can only ever be one of the new-grid keys once an org is
// actually subscribed to a new-grid plan, which cannot happen before the
// admin flip creates those billing_plans rows + Stripe prices. So this can
// never mis-highlight a card before the flip.
function resolveCurrentPlanId(ent) {
  if (!ent) return null;
  const planKey = ent.plan || ent.planKey;
  if (planKey === 'posting') return 'posting';
  if (planKey && NEW_PLAN_ID_BY_KEY[planKey]) return NEW_PLAN_ID_BY_KEY[planKey];
  if (planKey && PLAN_ID_BY_KEY[planKey]) return PLAN_ID_BY_KEY[planKey];
  const name = String(ent.planName || planKey || '').toLowerCase();
  if (name.includes('pilot')) return 'pilot';
  if (name.includes('growth') || name.includes('operator')) return 'growth';
  if (name.includes('studio') || name.includes('multi')) return 'studio';
  if (name.includes('enterprise')) return 'enterprise';
  return null;
}

const IconStar = (p) => (
  <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M12 3 13.8 8.4 19.5 8.6 15 12.1 16.6 17.6 12 14.3 7.4 17.6 9 12.1 4.5 8.6 10.2 8.4z" />
  </svg>
);
const IconCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="m5 12 5 5 9-11" /></svg>
);
const IconArrow = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
// The Scheduler band's "no AI" glyph — a plain circle-slash, not a checkmark
// (never implies "included"), rendered inline before postingPlan.noAiLine.
const IconNoAi = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m5 19 14-14" /></svg>
);

// TASK-331 (D5): billing dates render language-neutral, same convention as
// Settings.jsx's cancelDateLabel/openCancelConfirm — numbers/months stay 'en'
// regardless of UI locale (matches invoices, next-charge dates elsewhere).
const formatBillingDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

// TASK-331 SPA fixes round 5 (MINOR 1): scroll a just-appeared banner into
// view and move focus to it — a mobile user who clicked Pro/Agency two
// viewports below the top of the grid would otherwise never see the
// switchAction/switchError/switchNotice feedback land. Respects
// prefers-reduced-motion (no smooth-scroll animation when the user has that
// set). `el` is expected to carry tabIndex={-1} (SwitchBanner below) so
// focus() doesn't throw and doesn't add a new tab stop to the page.
function focusBanner(el) {
  if (!el) return;
  const reducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
  el.focus({ preventScroll: true });
}

// TASK-331 SPA fixes round 5 (MINOR 5): the gold action/error banners
// (switchAction/switchError) and the success notice below them shared
// duplicated container markup + inline styles that had already drifted once
// (the BLOCKER this round fixed). This extracts the shared shell only — the
// actions area varies per banner (switchAction always renders two buttons,
// switchError renders zero or one, switchNotice renders none), so it's a
// `children` slot rather than a fixed cta/onCta pair, keeping every existing
// style value byte-identical (no visual change).
function SwitchBanner({ tone = 'gold', message, refEl, children }) {
  const isGold = tone === 'gold';
  return (
    <div
      ref={refEl}
      // MINOR 8 (round 6): no role="alert"/"status" here — focusBanner already
      // moves programmatic focus onto this element (tabIndex={-1} below makes
      // that legal), which itself announces the content to a screen reader.
      // Adding a live-region role on top announced it a second time.
      tabIndex={-1}
      style={isGold ? {
        margin: '0 0 18px', padding: '14px 18px', borderRadius: 12,
        background: 'color-mix(in srgb, var(--gold, #C9A961) 12%, var(--bg-subtle))',
        border: '1px solid color-mix(in srgb, var(--gold, #C9A961) 45%, transparent)',
        fontSize: 13.5, lineHeight: 1.55, color: 'var(--text)',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      } : {
        margin: '0 0 18px', padding: '11px 16px', borderRadius: 12,
        background: 'var(--success-soft)', border: '1px solid var(--success)',
        fontSize: 13, lineHeight: 1.5, color: 'var(--success-text, var(--success))', textAlign: 'center',
      }}
    >
      <span style={isGold ? { flex: '1 1 320px' } : undefined}>{message}</span>
      {children}
    </div>
  );
}

export default function Plans() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getPlanDisplay, getNewPlanDisplay, getPostingPlanDisplay, newGridActive, postingPlanActive, managedPaymentsNewSubscriptions: catalogManagedPayments } = usePlanCatalog();
  const dashboard = useDashboardData();
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingReady, setBillingReady] = useState(false);
  const [billingLoadFailed, setBillingLoadFailed] = useState(false);
  const [billingScopeAccountId, setBillingScopeAccountId] = useState(null);
  const billingRequestRef = useRef(0);
  // TASK-331 (fix #6): the raw Stripe status alone can't tell a genuinely
  // live subscriber from a grace-window-expired lapsed one — a past_due-lapsed
  // org's billingStatus still reads 'past_due' (see entitlement-types.ts).
  // accessState is the one derived field that gets this right.
  const [accessState, setAccessState] = useState(null);
  // P1 of GH-1133 (#1150): with accessState 'new', decides whether a checkout
  // started here gets the card wall's trial (see newAccountTrialCheckoutOptions).
  const [hasSubscriptionHistory, setHasSubscriptionHistory] = useState(false);
  // Human-readable plan name from entitlements — used by the new grid's
  // legacy-plan banner (a legacy subscriber's plan has no card in that grid).
  const [entPlanName, setEntPlanName] = useState(null);
  // TASK-331 (D2/D5): whether the live subscription is scheduled to cancel at
  // period end, and (while trialing) the entitlements `currentPeriodEnd` —
  // which IS the trial end date for a trialing org (gateway routes/billing.ts
  // handleGetEntitlements; entitlement-repository.ts's currentPeriodEnd
  // comment: "a 7-day trial's current_period_end is the trial end"). There is
  // no separate trialEndsAt field on the entitlements payload — this is it.
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  // TASK-331 phase 2 (Subscription Schedules) — a downgrade already accepted
  // by change-plan but deferred to a future date (accessState
  // 'downgrade_scheduled'); the org's plan/limits stay CURRENT until then.
  // { plan, effectiveAt } | null — see lib/planSwitchTrial.js's
  // resolveScheduledDowngrade for the shared (both-or-neither) read.
  const [scheduledPlan, setScheduledPlan] = useState(null);
  const [scheduledPlanEffectiveAt, setScheduledPlanEffectiveAt] = useState(null);
  const [undoingDowngrade, setUndoingDowngrade] = useState(false);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Which new-grid card's checkout errored — the legacy grid only ever has one
  // sellable (featured) card so it doesn't need this; the new grid has four.
  const [attemptedPlanId, setAttemptedPlanId] = useState(null);
  // Success notice after an in-place plan switch (new grid only).
  const [switchNotice, setSwitchNotice] = useState(null);
  // MINOR 7 (round 6): switchNotice is a plain string — two switches in a row
  // that happen to resolve to the identical message (e.g. two noChange
  // outcomes) would otherwise skip the scroll/focus effect below entirely
  // (bumped to the same value twice is not a change React re-renders for).
  // Incremented once per switch that actually sets a notice message, and
  // added to that effect's deps, so the banner re-announces every time.
  const [noticeSeq, setNoticeSeq] = useState(0);
  // TASK-331 (SPA follow-up): a requiresAction outcome from change-plan — the
  // prorated/first invoice needs the customer (3DS, a declined card). The
  // plan has NOT changed yet, so this is tracked separately from
  // switchNotice (a completed-switch confirmation) and the two are always
  // mutually exclusive. { planKey, message, hostedInvoiceUrl } | null.
  const [switchAction, setSwitchAction] = useState(null);
  // TASK-331 (SPA follow-up): a change-plan failure with a machine-readable
  // `err.details.reason` this page knows how to explain — see
  // lib/planSwitchTrial.js's resolveSwitchError for the four recognized
  // reasons. Kept separate from the generic per-card `error` string (below)
  // so it can render its own CTA (add card / try again / billing settings)
  // instead of a bare message; a reason resolveSwitchError doesn't recognize
  // still falls back to `error`. { planKey, reason, message, ctaLabel, cta } | null.
  const [switchError, setSwitchError] = useState(null);
  // TASK-331 (SPA follow-up): sticky once a SUBSCRIPTION_ENDED change-plan
  // error is seen — the trial subscription is gone, so every card's CTA must
  // route through checkout (handleSubscribeNew) for the rest of this page
  // visit, never retry change-plan against a subscription that no longer
  // exists. Folded into canSwitchInPlace below; never reset back to false
  // (a fresh entitlements fetch — i.e. a reload — is what clears it).
  const [subscriptionEndedDuringSwitch, setSubscriptionEndedDuringSwitch] = useState(false);
  // TASK-331 SPA fixes round 5 (MINOR 1): refs for the three switch banners
  // (SwitchBanner below) — scrolled into view + focused whenever their state
  // sets from null to a value, so the feedback is discoverable however far
  // down the grid the triggering click was.
  const switchActionRef = useRef(null);
  const switchErrorRef = useRef(null);
  const switchNoticeRef = useRef(null);
  useEffect(() => { if (switchAction) focusBanner(switchActionRef.current); }, [switchAction]);
  useEffect(() => { if (switchError) focusBanner(switchErrorRef.current); }, [switchError]);
  useEffect(() => { if (switchNotice) focusBanner(switchNoticeRef.current); }, [switchNotice, noticeSeq]);
  // Open/failed invoice on the current sub — this page's most useful action for
  // that user is FINISHING the payment, not switching plans (owner 2026-07-11:
  // a declined pay-now landed here with only "Switch plan" buttons in sight).
  const [paymentIssue, setPaymentIssue] = useState(null);
  // TASK-329 (owner decision, item 1/2): the Scheduler add-on overage field
  // group (slice B) — the ONE source this page reads for
  // connected/unitAmount/currency; each card's `included` count comes from
  // usePlanCatalog instead (per-card price-provenance line below), except
  // the org's OWN current plan, which reads its included count straight off
  // this field group (the exact number slice B resolved for real billing —
  // admin-override- and trial-aware — rather than the catalog's static
  // fallback).
  const [socialAccountsEnt, setSocialAccountsEnt] = useState(null);
  // MINOR (round 2 fix): stores only WHICH card's sheet is open, not a
  // frozen pricing snapshot — the sheet's `pricing` prop is recomputed live
  // from `socialAddonPricingFor` on every render (below), so a disconnect
  // inside the sheet (which refetches socialAccountsEnt via onDisconnected)
  // updates the sheet's own header the same way it already updates the
  // card underneath it, instead of the header staying stuck on the number
  // that was true when the sheet first opened.
  const [addonSheet, setAddonSheet] = useState({ open: false, planId: null, planName: null });
  // MINOR 4 (round 2 fix): a real, persistent ref — NOT a fresh `{ current:
  // ... }` object literal recreated on every render (the previous shape)
  // — so ConnectedAccountsSheet's own reposition-on-scroll/resize effect,
  // which depends on `anchorRef` for its dependency array, can actually
  // tell "same anchor" apart from "different anchor" instead of re-running
  // every render.
  const addonSheetAnchorRef = useRef(null);
  // Enterprise CTA — opens the shared ContactOptionsModal (Discord + email),
  // which posts to the same public contact endpoint (replaces the old
  // inline form / mailto: handoff).
  const [entOpen, setEntOpen] = useState(false);
  // TASK-298: "See all plans" toggle — collapsed by default on both plan
  // surfaces (component-local, no persistence, no URL param). Expanding only
  // appends the Agency card and swaps the grid's cols-3 -> cols-4 modifier.
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [schedulerReview, setSchedulerReview] = useState(null);
  const [schedulerReviewAccounts, setSchedulerReviewAccounts] = useState(null);
  const [schedulerReviewAccountId, setSchedulerReviewAccountId] = useState(null);
  const [schedulerReviewLoading, setSchedulerReviewLoading] = useState(false);
  const [schedulerReviewError, setSchedulerReviewError] = useState(false);
  const [schedulerReviewChanged, setSchedulerReviewChanged] = useState(false);
  const [reviewErrorTarget, setReviewErrorTarget] = useState(null);
  const schedulerReviewRequestRef = useRef(0);
  const reviewReadRef = useRef(null);
  const reviewGenerationRef = useRef(0);
  const activeReviewRef = useRef(null);
  const switchSubmitRef = useRef(null);
  // One synchronous owner spans every billing read/mutation started from this
  // page. React state only mirrors it for disabled styling; the ref is the
  // authority that rejects saved/double handlers before they can issue I/O.
  const billingOperationRef = useRef(null);
  const [billingOperationBusy, setBillingOperationBusy] = useState(false);
  // A submitted invoice survives another review on the same account, but
  // its responses must never outlive this route/account.
  const schedulerLifecycleRef = useRef(0);
  // Publish authorization only on commit; discarded renders cannot make the
  // visible plan buttons stale.
  const plansIdentity = useMemo(() => ({ accountId: dashboard.accountId }), [dashboard.accountId]);
  const renderContextRef = useRef(null);
  useLayoutEffect(() => {
    const committed = { ...plansIdentity, identity: plansIdentity, mounted: true };
    renderContextRef.current = committed;
    return () => { committed.mounted = false; };
  }, [plansIdentity]);
  const operationIsCurrent = (owner) => Boolean(owner) && billingOperationRef.current === owner
    && owner.lifecycle === schedulerLifecycleRef.current
    && renderContextRef.current?.mounted
    && renderContextRef.current.identity === owner.identity
    && renderContextRef.current.accountId === owner.accountId
    && (dashboard.isEntitlementsScopeCurrent
      ? dashboard.isEntitlementsScopeCurrent(owner.scope)
      : owner.accountId === dashboard.accountId);
  const acquireBillingOperation = (kind, identity = plansIdentity) => {
    const committed = renderContextRef.current;
    if (billingOperationRef.current || !dashboard.accountId || !committed?.mounted
      || committed.identity !== identity || committed.accountId !== dashboard.accountId) return null;
    const owner = {
      kind,
      identity,
      accountId: dashboard.accountId,
      lifecycle: schedulerLifecycleRef.current,
      scope: dashboard.captureEntitlementsScope?.() || { account: dashboard.accountId },
    };
    billingOperationRef.current = owner;
    setBillingOperationBusy(true);
    return owner;
  };
  const releaseBillingOperation = (owner) => {
    if (!operationIsCurrent(owner)) return;
    billingOperationRef.current = null;
    setBillingOperationBusy(false);
  };
  // TASK-313: ?plan=<key> intent captured at boot (main.jsx) — read once, same
  // as PlanSelectScreen.jsx. Only ever highlights; a card the org is already
  // on renders its normal "Current" state instead (see intentPlanKey below).
  const [planIntentKey] = useState(() => getPlanIntent());

  useEffect(() => {
    const request = ++billingRequestRef.current;
    const accountId = dashboard.accountId;
    let cancelled = false;
    setBillingReady(false);
    setBillingLoadFailed(false);
    setBillingScopeAccountId(null);
    setCurrentPlanId(null);
    setBillingStatus(null);
    setEntPlanName(null);
    setPaymentIssue(null);
    setSocialAccountsEnt(null);
    setAccessState(null);
    setHasSubscriptionHistory(false);
    setCancelAtPeriodEnd(false);
    setTrialEndsAt(null);
    setScheduledPlan(null);
    setScheduledPlanEffectiveAt(null);
    setCard(null);
    api.billing.getEntitlements({ fresh: true })
      .then((ent) => {
        if (cancelled || request !== billingRequestRef.current || accountId !== dashboard.accountId) return;
        if (!isRecognizedBillingState(ent)) throw new Error('Invalid entitlements response');
        setCurrentPlanId(resolveCurrentPlanId(ent));
        setBillingStatus(ent?.billingStatus || ent?.data?.billingStatus || null);
        setEntPlanName(ent?.planName || ent?.data?.planName || null);
        setPaymentIssue(ent?.paymentIssue || ent?.data?.paymentIssue || null);
        setSocialAccountsEnt(ent?.socialAccounts || ent?.data?.socialAccounts || null);
        setAccessState(ent?.accessState || ent?.data?.accessState || null);
        setHasSubscriptionHistory((ent?.hasSubscriptionHistory ?? ent?.data?.hasSubscriptionHistory) === true);
        setCancelAtPeriodEnd(!!(ent?.cancelAtPeriodEnd ?? ent?.data?.cancelAtPeriodEnd));
        setTrialEndsAt(ent?.currentPeriodEnd ?? ent?.data?.currentPeriodEnd ?? null);
        const scheduledDowngrade = resolveScheduledDowngrade(ent) || resolveScheduledDowngrade(ent?.data);
        setScheduledPlan(scheduledDowngrade?.plan ?? null);
        setScheduledPlanEffectiveAt(scheduledDowngrade?.effectiveAt ?? null);
        setBillingScopeAccountId(accountId);
        setBillingReady(true);
        setBillingLoadFailed(false);
      })
      .catch(() => {
        if (cancelled || request !== billingRequestRef.current) return;
        setBillingReady(true);
        setBillingLoadFailed(true);
      });
    // The saved card, so a trial user can see what "Pay now" will charge.
    api.billing.getPaymentMethod()
      .then((r) => {
        if (!cancelled && request === billingRequestRef.current && accountId === dashboard.accountId) {
          setCard(r?.card ?? r?.data?.card ?? null);
        }
      })
      .catch(() => { /* no card / Stripe blip — pay-now note degrades gracefully */ });
    return () => { cancelled = true; };
  }, [dashboard.accountId]);

  useEffect(() => {
    if (postingPlanActive) dashboard.invalidateResource?.('socialAccounts');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingPlanActive]);

  // TASK-313: Agency sits behind the collapsed "See all plans" toggle —
  // expand it so an incoming ?plan=agency intent is actually visible.
  useEffect(() => {
    if (planIntentKey === 'agency') setShowAllPlans(true);
  }, [planIntentKey]);

  // TASK-313: scroll the intended plan into view once it's known to be
  // reachable (re-runs after the Agency expand above mounts its card).
  useEffect(() => {
    if (!planIntentKey || typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(() => {
      document.querySelector(`[data-plan="${planIntentKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [planIntentKey, showAllPlans]);

  // On a 7-day trial the user IS on the selected plan but hasn't been charged.
  // "Pay now" hands off to Settings' existing activation confirmation.
  const isTrialing = billingStatus === 'trialing';

  // TASK-331 (fix #6): a lapsed org (accessState 'lapsed') is neither a
  // "genuinely live subscriber" (change-plan on it is the wrong action — the
  // org isn't paying, has 0 limits, and change-plan's proration math assumes
  // an active subscription) nor a plain new signup. Route it by whether the
  // Stripe subscription is still live with an open invoice (readPaymentIssue's
  // hasOpenInvoice, same discriminator UpgradeModal's M3 fix uses) — settle
  // that invoice/portal rather than switch plans or start a checkout that
  // would just hit alreadySubscribed and dead-end; a genuinely canceled sub
  // (hasOpenInvoice false) falls through to the existing checkout branch
  // below, which is exactly right for it.
  const isLapsed = accessState === 'lapsed';
  const lapsedHasOpenInvoice = isLapsed && !!paymentIssue?.hasOpenInvoice;

  // TASK-331 phase-1-fixes round 3 (fix #3): a past_due-lapsed org's Stripe
  // subscription is still LIVE (not canceled) even though accessState reads
  // 'lapsed' (the grace window expired) — billingStatus stays 'past_due' for
  // this case specifically because entitlement-types.ts's status mapper only
  // collapses canceled/unpaid/incomplete_expired into 'inactive', never
  // past_due. When such an org also has no open invoice (hasOpenInvoice
  // false — a Smart Retry cycle can exhaust without leaving one open, or the
  // account may only be recoverable via the portal), checkout is still the
  // WRONG action: create-checkout's maybeConvertExistingSubscription finds
  // the live subscription and throws ALREADY_SUBSCRIBED (the exact dead-end
  // fix #6 above already fixed for the open-invoice case). Route to the
  // billing portal instead — the SAME api.billing.createPortalSession() call
  // UpgradeModal's renew-variant handleUpdateCard() uses.
  const lapsedPastDueNoInvoice = isLapsed && billingStatus === 'past_due' && !lapsedHasOpenInvoice;
  const [portalBusy, setPortalBusy] = useState(false);
  const handleLapsedRenewPortal = async () => {
    if (portalBusy) return;
    setError(null);
    setPortalBusy(true);
    try {
      const result = await api.billing.createPortalSession({
        returnUrl: typeof window !== 'undefined' ? window.location.href : null,
      });
      const portalUrl = result?.portalUrl || result?.data?.portalUrl;
      if (!portalUrl) throw new Error('missing_portal_url');
      window.location.assign(portalUrl);
    } catch (_) {
      setError(t('paymentIssue.portalError'));
      setPortalBusy(false);
    }
  };

  // Live paid subscription (NOT trialing — that has its own treatment). Mirrors
  // Settings' ACTIVE_BILLING_STATUSES minus 'trialing'. Used by the new grid to
  // route non-current cards to change-plan instead of checkout: the checkout
  // endpoint's maybeConvertExistingSubscription acts on the EXISTING
  // subscription and never reads the requested planKey, so calling it with a
  // live sub either silently charges the WRONG plan (trial) or silently no-ops
  // (active) — both money bugs. Excludes `isLapsed` even though a past_due-
  // lapsed org's raw billingStatus still reads 'past_due' (see accessState
  // comment above) — that case is handled by lapsedHasOpenInvoice instead.
  const hasLiveSub = !isLapsed && (billingStatus === 'active' || billingStatus === 'checkout_completed' || billingStatus === 'past_due');
  // TASK-331 (D1/D3/D5): a trialing org can now switch plans in place too —
  // change-plan.ts no longer charges a trialing subscription (proration_
  // behavior:'none'; ending the trial today only for the one target plan
  // that never has one, Scheduler). hasLiveSub's own semantics (and the
  // checkout guard it protects) are unchanged.
  // TASK-331 (SPA follow-up): once change-plan has reported SUBSCRIPTION_ENDED
  // for this session, never offer change-plan again — the trial subscription
  // it would act on is gone, so every card must fall through to the checkout
  // (handleSubscribeNew) branch below instead.
  const canSwitchInPlace = !subscriptionEndedDuringSwitch && canSwitchPlanInPlace({ isTrialing, hasLiveSub });

  // MINOR 6 (round 7 review): cancel wins over a scheduled downgrade — same
  // "cancel_scheduled outranks downgrade_scheduled" ordering Settings.jsx's
  // own `scheduledDowngrade = !cancelAtPeriodEnd ? resolveScheduledDowngrade(...)
  // : null` already applies. A subscription that is ALSO ending outright is
  // the more urgent fact; a "Scheduled · <date>" badge on a card the
  // subscription won't even be around to reach by then would be misleading.
  const scheduledPlanForBadge = cancelAtPeriodEnd ? null : scheduledPlan;
  // Committed pending facts let a saved Undo handler prove it still names the
  // schedule currently on screen. In particular, A -> B -> A and a replacement
  // schedule on A both receive a new committed identity/fact tuple.
  const pendingScheduleIdentity = useMemo(() => ({
    accountIdentity: plansIdentity,
    plan: scheduledPlanForBadge,
    effectiveAt: scheduledPlanForBadge ? scheduledPlanEffectiveAt : null,
  }), [plansIdentity, scheduledPlanForBadge, scheduledPlanEffectiveAt]);
  const committedScheduleRef = useRef(null);
  useLayoutEffect(() => {
    committedScheduleRef.current = {
      identity: pendingScheduleIdentity,
      plan: scheduledPlanForBadge,
      effectiveAt: scheduledPlanForBadge ? scheduledPlanEffectiveAt : null,
    };
  }, [pendingScheduleIdentity, scheduledPlanForBadge, scheduledPlanEffectiveAt]);

  // Same "open the hosted invoice, else the portal" fallback UpgradeModal's
  // M3 fix and this page's own top-of-page paymentIssue banner already use.
  const handleLapsedPayInvoice = () => {
    if (paymentIssue?.invoiceUrl) window.open(paymentIssue.invoiceUrl, '_blank', 'noopener');
    else navigate('/app/settings?tab=billing');
  };

  // New grid + a live/trialing subscription on a plan with NO card in the new
  // grid (brand_pilot / marketing_operator / multi_brand / enterprise — any
  // resolved id that isn't a NEW_PLAN_DEFS id) → surface a quiet banner so the
  // user understands why no card reads "Current".
  const currentPlanIsLegacy = !!currentPlanId && currentPlanId !== 'posting' && !NEW_PLAN_DEFS.some((p) => p.id === currentPlanId);
  const showLegacyBanner = newGridActive && currentPlanIsLegacy && (hasLiveSub || isTrialing);
  const legacyPlanName = entPlanName
    || PLAN_DEFS.find((p) => p.id === currentPlanId)?.name
    || currentPlanId;

  const tx = (key, fallback, vars) => (key ? t(`settings.${key}`, vars) : fallback);

  // TASK-298: single "all plans in display order" source — the Scheduler
  // (Track 1) band plus the three-tier AI grid (Track 2) and the Agency
  // "See all plans" extra. 'posting' only ever appears in publishTrack.
  const { publishTrack, aiTrack, extraTrack } = buildPlanTracks({
    getNewPlanDisplay,
    getPostingPlanDisplay,
    postingPlanActive,
    tx,
    t,
    connectedSocials: dashboard.socialAccounts,
  });
  const postingDisplay = getPostingPlanDisplay();
  const reviewTargetFor = (planKey) => {
    if (planKey === 'posting') return publishTrack ? {
      planKey,
      name: t('postingPlan.name'),
      priceCents: postingDisplay?.priceCents,
      priceFormatted: publishTrack.line.unitFormatted,
    } : null;
    const entry = [...aiTrack, ...extraTrack].find((candidate) => candidate.id === planKey);
    return entry ? {
      planKey,
      name: entry.translated.name,
      priceCents: entry.priceCents,
      priceFormatted: entry.translated.price,
    } : null;
  };
  const buildSwitchReview = ({ targetKey, ent = null, connectedSocials = null }) => {
    const target = reviewTargetFor(targetKey);
    if (!target) return null;
    const source = ent || {
      billingStatus, accessState, cancelAtPeriodEnd,
      plan: currentPlanId, scheduledPlan, scheduledPlanEffectiveAt,
    };
    const nextScheduled = ent ? resolveScheduledDowngrade(source) : { plan: scheduledPlan, effectiveAt: scheduledPlanEffectiveAt };
    return buildPlanSwitchReview({
      accountId: dashboard.accountId,
      billingStatus: source.billingStatus || source.subscriptionStatus || null,
      accessState: source.accessState,
      currentPlanId: ent ? resolveCurrentPlanId(source) : currentPlanId,
      cancelAtPeriodEnd: source.cancelAtPeriodEnd,
      scheduledPlan: nextScheduled?.plan ?? null,
      scheduledPlanEffectiveAt: nextScheduled?.effectiveAt ?? null,
      targetPlanKey: target.planKey,
      targetName: target.name,
      targetPriceCents: target.priceCents,
      targetPriceFormatted: target.priceFormatted,
      postingDisplay,
      connectedSocials: targetKey === 'posting' ? connectedSocials : null,
    });
  };
  const billingScopeCurrent = billingReady
    && !billingLoadFailed
    && billingScopeAccountId === dashboard.accountId;
  // Plans marketing belongs only to genuine acquisition state. Reuse the
  // page's existing trial/live predicates (which already include valid
  // cancel_scheduled/downgrade_scheduled subscriptions via their live Stripe
  // status) rather than introducing another subscription-status model.
  const hasPlansSubscriber = billingScopeCurrent && (isTrialing || hasLiveSub);
  // GH-912 (Stripe Managed Payments): shown only when this page's CTAs start
  // a NEW Stripe checkout (no in-place switch, no lapsed invoice/portal
  // path) — only a new subscription is sold through Link. The flag is
  // environment-wide, so the shared entitlements snapshot is enough here.
  const showManagedPaymentsNote = billingScopeCurrent
    && !canSwitchInPlace
    && !lapsedHasOpenInvoice
    && !lapsedPastDueNoInvoice
    && resolveManagedPaymentsNewSubscriptions({ catalogFlag: catalogManagedPayments, entitlements: dashboard.entitlements });
  const currentSchedulerReview = schedulerReview ? buildSwitchReview({
    targetKey: schedulerReview.planKey,
    connectedSocials: schedulerReview.planKey === 'posting' && schedulerReviewAccountId === dashboard.accountId
      ? schedulerReviewAccounts
      : null,
  }) : null;
  const schedulerReviewCurrent = billingScopeCurrent
    && !schedulerReviewLoading
    && planSwitchReviewMatches(schedulerReview, currentSchedulerReview);
  const schedulerBillingEligible = billingScopeCurrent && !!buildSwitchReview({ targetKey: 'posting', connectedSocials: [] });

  useEffect(() => {
    setSchedulerReview(null);
    setSchedulerReviewAccounts(null);
    setSchedulerReviewAccountId(null);
    setSchedulerReviewLoading(false);
    setSchedulerReviewError(false);
    setSchedulerReviewChanged(false);
    setReviewErrorTarget(null);
    activeReviewRef.current = null;
    reviewReadRef.current = null;
    switchSubmitRef.current = null;
    billingOperationRef.current = null;
    setBillingOperationBusy(false);
    setUndoingDowngrade(false);
    setBusy(false);
    setAttemptedPlanId(null);
    setError(null);
    setSwitchAction(null);
    setSwitchNotice(null);
    setSwitchError(null);
    setSubscriptionEndedDuringSwitch(false);
    // The dashboard provider can outlive this route. Its account scope alone
    // cannot invalidate a confirmation read after Plans has unmounted.
    return () => {
      schedulerReviewRequestRef.current += 1;
      schedulerLifecycleRef.current += 1;
      activeReviewRef.current = null;
      reviewReadRef.current = null;
      switchSubmitRef.current = null;
      billingOperationRef.current = null;
    };
  }, [dashboard.accountId]);

  const refreshSchedulerReview = async (
    targetKey,
    expectedAuthorization = null,
    openingContext = null,
    existingOperation = null,
    retainOperation = false,
  ) => {
    if (busy || reviewReadRef.current || !dashboard.accountId) return false;
    if (openingContext && (!renderContextRef.current?.mounted
      || renderContextRef.current.identity !== openingContext)) return false;
    if (expectedAuthorization && activeReviewRef.current !== expectedAuthorization) return false;
    const operation = existingOperation || acquireBillingOperation('review-read', openingContext || plansIdentity);
    if (!operation || !operationIsCurrent(operation)) return false;
    const readOwner = {};
    reviewReadRef.current = readOwner;
    const request = ++schedulerReviewRequestRef.current;
    const lifecycle = schedulerLifecycleRef.current;
    const scope = dashboard.captureEntitlementsScope?.() || { account: dashboard.accountId };
    const lifecycleIsCurrent = () => lifecycle === schedulerLifecycleRef.current
      && (dashboard.isEntitlementsScopeCurrent
        ? dashboard.isEntitlementsScopeCurrent(scope)
        : scope.account === dashboard.accountId);
    const scopeIsCurrent = () => request === schedulerReviewRequestRef.current && lifecycleIsCurrent();
    setSchedulerReviewLoading(true);
    setSchedulerReviewError(false);
    setSchedulerReviewChanged(false);
    setReviewErrorTarget(null);
    setSchedulerReviewAccounts(null);
    setSchedulerReviewAccountId(null);
    try {
      api.invalidateCache?.(['/social/accounts']);
      const [ent, socialResponse] = await Promise.all([
        api.billing.getEntitlements({ fresh: true }),
        targetKey === 'posting' ? api.socialAccounts.list() : Promise.resolve(null),
      ]);
      if (!scopeIsCurrent()) return false;
      if (!isRecognizedBillingState(ent) || (targetKey === 'posting' && !Array.isArray(socialResponse?.accounts))) {
        throw new Error('Invalid billing review response');
      }
      const nextPlanId = resolveCurrentPlanId(ent);
      const nextScheduled = resolveScheduledDowngrade(ent);
      const nextReview = buildSwitchReview({ targetKey, ent, connectedSocials: socialResponse?.accounts ?? null });
      if (!nextReview) throw new Error('Billing state is not eligible for this change');

      if (expectedAuthorization && activeReviewRef.current !== expectedAuthorization) return false;

      setCurrentPlanId(nextPlanId);
      setBillingStatus(ent.billingStatus || ent.subscriptionStatus || null);
      setEntPlanName(ent.planName || null);
      setPaymentIssue(ent.paymentIssue || null);
      setSocialAccountsEnt(ent.socialAccounts || null);
      setAccessState(ent.accessState);
      setHasSubscriptionHistory(ent.hasSubscriptionHistory === true);
      setCancelAtPeriodEnd(ent.cancelAtPeriodEnd);
      setTrialEndsAt(ent.currentPeriodEnd ?? null);
      setScheduledPlan(nextScheduled?.plan ?? null);
      setScheduledPlanEffectiveAt(nextScheduled?.effectiveAt ?? null);
      setBillingScopeAccountId(dashboard.accountId);
      setBillingReady(true);
      setBillingLoadFailed(false);
      setSchedulerReviewAccounts(socialResponse?.accounts ?? null);
      setSchedulerReviewAccountId(targetKey === 'posting' ? dashboard.accountId : null);
      setSchedulerReview(nextReview);
      const authorization = {
        generation: ++reviewGenerationRef.current,
        accountId: dashboard.accountId,
        targetKey,
        review: nextReview,
      };
      activeReviewRef.current = authorization;
      if (expectedAuthorization && !planSwitchReviewMatches(expectedAuthorization.review, nextReview)) {
        setSchedulerReviewChanged(true);
        return false;
      }
      return { authorization, isCurrent: scopeIsCurrent, lifecycleIsCurrent };
    } catch (_) {
      if (scopeIsCurrent()) {
        setSchedulerReviewError(true);
        setReviewErrorTarget(targetKey);
      }
      return false;
    } finally {
      if (reviewReadRef.current === readOwner) {
        reviewReadRef.current = null;
        if (scopeIsCurrent()) setSchedulerReviewLoading(false);
      }
      if (!retainOperation) releaseBillingOperation(operation);
    }
  };

  const openPlanReview = async (targetKey) => {
    if (busy || (targetKey === 'posting' && !schedulerBillingEligible)) return;
    await refreshSchedulerReview(targetKey, null, plansIdentity);
  };

  const confirmSchedulerSwitch = async () => {
    const authorization = activeReviewRef.current;
    if (busy || !authorization || authorization.review !== schedulerReview || !schedulerReviewCurrent) return;
    const operation = acquireBillingOperation('review-confirm', plansIdentity);
    if (!operation) return;
    const confirmedReview = schedulerReview;
    const refreshedReview = await refreshSchedulerReview(confirmedReview.planKey, authorization, null, operation, true);
    if (!refreshedReview || !refreshedReview.isCurrent()) {
      releaseBillingOperation(operation);
      return;
    }
    setSchedulerReview(null);
    operation.kind = 'switch-submit';
    handleSwitchPlan(confirmedReview.planKey, refreshedReview, operation);
  };

  const closeSchedulerReview = () => {
    if (busy) return;
    if (!schedulerReview || activeReviewRef.current?.review !== schedulerReview) return;
    const operation = billingOperationRef.current;
    schedulerReviewRequestRef.current += 1;
    reviewReadRef.current = null;
    activeReviewRef.current = null;
    setSchedulerReview(null);
    setSchedulerReviewLoading(false);
    setSchedulerReviewError(false);
    setSchedulerReviewChanged(false);
    setReviewErrorTarget(null);
    if (operation?.kind === 'review-confirm') releaseBillingOperation(operation);
  };

  const reviewTrialActivation = () => navigate('/app/settings?tab=billing');
  const aiCards = showAllPlans ? [...aiTrack, ...extraTrack] : aiTrack;
  // Price-cents lookup used by resolveSwitchLabel below — always the FULL
  // set (independent of the collapsed/expanded toggle) so a comparison
  // against a collapsed Agency card (e.g. the org's current plan) still
  // resolves correctly.
  const allAiCardsForPricing = [...aiTrack, ...extraTrack];
  const currentPriceCentsForComparison = currentPlanId === 'posting'
    ? (publishTrack?.priceCents ?? null)
    : (allAiCardsForPricing.find((c) => c.id === currentPlanId)?.priceCents ?? null);

  // TASK-329 (owner decision, item 1): the Scheduler add-on price-provenance
  // line under each AI card. `connected`/`unitAmount`/`currency` (and the
  // hidden/includes/priced `mode`) are the ONE shared source — resolved
  // through resolveSocialAddonState (round 2 fix, blocker 1) from
  // socialAccountsEnt, GET /api/billing/entitlements' slice-B field group —
  // `included` is the org's OWN live count (also from that state) for the
  // card matching its CURRENT plan, else the plan catalog's per-plan limit
  // (usePlanCatalog) for every other candidate card.
  const socialAddonState = resolveSocialAddonState(socialAccountsEnt);
  const socialAddonPricingFor = (planId, planName) => {
    if (socialAddonState.mode === 'hidden') return null;
    const included = planId === currentPlanId
      ? socialAddonState.included
      : resolveIncludedForPlanCard(planId, getNewPlanDisplay(planId)?.limits);
    return resolveSocialAddonPricing({
      mode: socialAddonState.mode,
      connected: socialAddonState.connected,
      included,
      unitAmount: socialAddonState.unitAmount,
      currency: socialAddonState.currency,
      planName,
      locale: i18n.language || 'en',
      t,
    });
  };

  // TASK-331 (fix #6 / M3 follow-up): startCheckout now throws a
  // distinguishable ALREADY_SUBSCRIBED error instead of silently redirecting
  // (lib/plans.js) — show the localized copy for that one code.
  const describeCheckoutError = (err) => (
    err?.code === 'ALREADY_SUBSCRIBED' ? t('upgradeModal.alreadySubscribed') : (err?.message || t('upgradeModal.checkoutError'))
  );

  // P1 of GH-1133 (#1150): a brand-new account (accessState 'new') reaches
  // this page through UpgradeModal "See all plans" and ~30 other links
  // whenever the card gate fails open. A bare startCheckout(planKey) has no
  // trial, so that account was charged at once; it now gets the card wall's
  // trial checkout. Every other account keeps the exact call it made before
  // (null → startCheckout(planKey)). The facts come from this page's own
  // read, else the shared entitlements snapshot; when neither is known (the
  // read failed or is still in flight) one fresh read decides, and if that
  // fails too no checkout starts — never a no-trial checkout by default.
  const resolveCheckoutOptions = async (planKey) => {
    const shared = dashboard.entitlements?.data || dashboard.entitlements;
    if (billingScopeCurrent) return newAccountTrialCheckoutOptions(planKey, { accessState, hasSubscriptionHistory });
    if (typeof shared?.accessState === 'string') return newAccountTrialCheckoutOptions(planKey, shared);
    let ent = null;
    try { ent = await api.billing.getEntitlements({ fresh: true }); } catch { /* handled below */ }
    if (!isRecognizedBillingState(ent?.data || ent)) throw new Error(t('upgradeModal.checkoutError'));
    return newAccountTrialCheckoutOptions(planKey, ent);
  };
  const startPlanCheckout = async (planKey, operation) => {
    const trialCheckout = await resolveCheckoutOptions(planKey);
    if (!operationIsCurrent(operation)) return false;
    if (trialCheckout) await startCheckout(planKey, trialCheckout);
    else await startCheckout(planKey);
    return true;
  };

  const handleSubscribe = async () => {
    if (busy) return;
    const operation = acquireBillingOperation('checkout', plansIdentity);
    if (!operation) return;
    setError(null);
    setBusy(true);
    try {
      await startPlanCheckout('brand_pilot', operation); // exact live Stripe path; navigates away
    } catch (err) {
      if (!operationIsCurrent(operation)) return;
      setError(describeCheckoutError(err));
      setBusy(false);
      releaseBillingOperation(operation);
    }
  };

  // New-grid equivalent of handleSubscribe — generic over planKey since all
  // four new cards (starter/growth/pro/agency) are self-service (unlike the
  // legacy grid, which only ever checks out 'brand_pilot'). startCheckout is
  // already planKey-generic (lib/plans.js), so no change was needed there.
  const handleSubscribeNew = async (planKey) => {
    if (busy) return;
    const operation = acquireBillingOperation('checkout', plansIdentity);
    if (!operation) return;
    setError(null);
    setSwitchNotice(null);
    // TASK-331 (SPA follow-up): clear any stale change-plan error banner
    // (e.g. SUBSCRIPTION_ENDED) once the user takes the suggested checkout
    // action — subscriptionEndedDuringSwitch itself stays sticky.
    setSwitchError(null);
    setAttemptedPlanId(planKey);
    setBusy(true);
    try {
      const started = await startPlanCheckout(planKey, operation);
      if (started && operationIsCurrent(operation)) clearPlanIntent(); // TASK-313: a successful checkout start consumes the intent.
    } catch (err) {
      if (!operationIsCurrent(operation)) return;
      setError(describeCheckoutError(err));
      setBusy(false);
      releaseBillingOperation(operation);
    }
  };

  // TASK-331 (D5): human-readable plan name for the post-switch confirmation
  // copy ("Switched to {{plan}}..."). Mirrors the exact name each AI card
  // itself renders (tx(keys.name, def.name)); 'posting' has no card entry.
  const resolvePlanDisplayName = (planKey) => {
    if (planKey === 'posting') return t('postingPlan.name');
    const entry = allAiCardsForPricing.find((c) => c.id === planKey);
    if (!entry) return planKey;
    const keys = NEW_PLAN_TEXT_KEYS[planKey] || {};
    return tx(keys.name, entry.name);
  };

  // MINOR 3 (round 7 review): resolvePlanDisplayName falls through to
  // returning the raw planKey itself when it finds no matching AI-grid card
  // entry (a legacy plan like 'pilot', or a lookup before
  // allAiCardsForPricing/entitlements are ready) — never a translated string
  // in that case. Detect exactly that "no match" case by comparing the
  // result against the input, and prefer entitlements' own human-readable
  // planName (entPlanName, already loaded — see legacyPlanName/the
  // paymentIssueBanner note above) over showing that raw backend key inside
  // a {{currentPlan}} interpolation.
  const resolveCurrentPlanLabel = (planKey) => {
    const resolved = resolvePlanDisplayName(planKey);
    return resolved === planKey ? (entPlanName || resolved) : resolved;
  };

  // In-place plan switch for a user with a LIVE subscription (new grid only).
  // Uses the EXISTING change-plan path end-to-end: api.billing.changePlan →
  // gateway POST /api/billing/change-plan → stripe-worker
  // /internal/billing/change-plan (DB-driven, accepts any billing_plans key —
  // legacy or new). This is the same endpoint Settings' historical change-plan
  // flow used; NOT the checkout endpoint, which must never be called with a
  // live subscription (see hasLiveSub note above). On success we re-fetch
  // entitlements (the POST clears the client's GET cache) so the "Current"
  // pill moves to the new card without a reload.
  const refetchSwitchedPlan = async (planKey, lifecycleIsCurrent = null) => {
    try {
      return await refetchEntitlementsUntilPlanMatches({
        expectedPlanKey: planKey,
        fetchEntitlements: (opts) => {
          if (lifecycleIsCurrent && !lifecycleIsCurrent()) throw new Error('Scheduler scope changed');
          return api.billing.getEntitlements(opts);
        },
        invalidateCache: (prefix) => {
          if (!lifecycleIsCurrent || lifecycleIsCurrent()) api.invalidateCache(prefix);
        },
      });
    } catch (err) {
      if (lifecycleIsCurrent && !lifecycleIsCurrent()) return null;
      throw err;
    }
  };

  const handleSwitchPlan = async (planKey, review = null, operation = null) => {
    const authorization = review?.authorization;
    if (busy || !operationIsCurrent(operation) || switchSubmitRef.current || !authorization || activeReviewRef.current !== authorization
      || authorization.targetKey !== planKey || authorization.accountId !== renderContextRef.current.accountId) return;
    const submitOwner = {};
    switchSubmitRef.current = submitOwner;
    const switchIsCurrent = () => switchSubmitRef.current === submitOwner
      && operationIsCurrent(operation)
      && review.lifecycleIsCurrent()
      && activeReviewRef.current === authorization
      && authorization.accountId === renderContextRef.current.accountId
      && authorization.targetKey === planKey;
    const finishSwitch = () => {
      if (switchSubmitRef.current !== submitOwner) return;
      switchSubmitRef.current = null;
      if (activeReviewRef.current === authorization) activeReviewRef.current = null;
      if (operationIsCurrent(operation)) {
        setBusy(false);
        releaseBillingOperation(operation);
      }
    };
    setError(null);
    setSwitchNotice(null);
    setSwitchAction(null);
    setSwitchError(null);
    setAttemptedPlanId(planKey);
    setBusy(true);
    try {
      if (!review.isCurrent() || !switchIsCurrent()) {
        finishSwitch();
        return;
      }
      if (!switchIsCurrent()) return;
      const result = await api.billing.changePlan({ newPlanKey: planKey });
      if (!switchIsCurrent()) return;
      const resultData = result?.data ?? result ?? {};
      const planLabel = resolvePlanDisplayName(planKey);

      // TASK-331 (SPA follow-up, staging bug report 2026-09-07): requiresAction
      // means the switch has NOT taken effect — the prorated/first invoice
      // needs the customer (3DS, a declined card). Never touch plan state or
      // consume the plan intent here: the org is still on its OLD plan until
      // that invoice is actually paid (webhook mirrors it then, same as any
      // other change-plan outcome). Surface the hosted invoice instead of
      // refetching entitlements that would just echo the unchanged plan back.
      if (resultData.requiresAction) {
        const notice = resolveSwitchNotice({
          requiresAction: true,
          hostedInvoiceUrl: resultData.hostedInvoiceUrl,
          planLabel,
          t,
        });
        setSwitchAction({ planKey, message: notice.message, hostedInvoiceUrl: notice.hostedInvoiceUrl, lifecycleIsCurrent: review?.lifecycleIsCurrent });
        finishSwitch();
        return;
      }

      // TASK-331 phase 2 (Subscription Schedules) — change-plan's
      // deferred-downgrade branch (scheduleDeferredDowngrade in
      // change-plan.ts): the switch WAS accepted, but the org keeps its
      // CURRENT plan/limits until resultData.effectiveAt, then flips
      // automatically. Never refetch-until-plan-matches here — the plan is
      // deliberately NOT changing today, so that retry loop would just spin
      // through its full 3 x 1.5s budget waiting for a match that never
      // comes. Update the scheduled-downgrade state directly from the
      // response instead, and invalidate the shared dashboard resource so
      // Settings/other screens pick up accessState 'downgrade_scheduled'
      // without a reload.
      if (resultData.scheduled === true) {
        // MINOR 3 (round 7 review): fall back to entPlanName when the
        // current plan has no AI-grid card match (legacy 'pilot', or a call
        // before entitlements finished loading) rather than interpolating
        // the raw backend plan key into the notice below.
        const currentPlanLabel = resolveCurrentPlanLabel(currentPlanId);
        const effectiveAtIso = resultData.effectiveAt || null;
        clearPlanIntent(); // the switch WAS accepted — same as any other successful outcome below.
        // MINOR 1 (round 7 review): both-or-neither guard via the same
        // resolveScheduledDowngrade the entitlements-sync path below already
        // uses — a malformed response carrying `plan` but no `effectiveAt`
        // (or vice versa) must never render a "Scheduled · " badge with an
        // empty date.
        const scheduledDowngrade = resolveScheduledDowngrade({
          scheduledPlan: resultData.plan || planKey,
          scheduledPlanEffectiveAt: effectiveAtIso,
        });
        setScheduledPlan(scheduledDowngrade?.plan ?? null);
        setScheduledPlanEffectiveAt(scheduledDowngrade?.effectiveAt ?? null);
        dashboard.invalidateResource?.('entitlements');
        // Round-8 minor: the notice follows the SAME both-or-neither guard as
        // the badge — a malformed `scheduled:true` response without a usable
        // date falls through to the plain "plan changed" copy instead of
        // rendering "Starter starts on . Until then…".
        setSwitchNotice(resolveSwitchNotice({
          scheduled: Boolean(scheduledDowngrade),
          planLabel,
          effectiveAt: scheduledDowngrade ? formatBillingDate(scheduledDowngrade.effectiveAt) : null,
          currentPlanLabel,
          t,
        }).message);
        setNoticeSeq((s) => s + 1);
        finishSwitch();
        return;
      }

      clearPlanIntent(); // TASK-313: a successful plan change consumes the intent.
      // TASK-331 (D5): the change-plan webhook mirror can trail this call's
      // own success response by a beat — retry the refetch (busting the
      // client cache each time) up to 3 x 1.5s until entitlements actually
      // reflects the new plan, instead of trusting a single refetch that can
      // still echo the OLD plan back. TASK-331 (SPA follow-up, 2026-09-08):
      // forward the `{ fresh: true }` refetchEntitlementsUntilPlanMatches
      // now always passes, so this ALSO busts the api-gateway's ~60s
      // per-org entitlements memo (see getEntitlements) — otherwise the
      // social-addon provenance line on this very page could still read the
      // pre-switch plan for up to a minute after the switch above succeeded.
      const entData = await refetchSwitchedPlan(planKey, switchIsCurrent);
      if (!switchIsCurrent()) return;
      if (entData) {
        setCurrentPlanId(resolveCurrentPlanId(entData));
        setBillingStatus(entData?.billingStatus || null);
        setEntPlanName(entData?.planName || null);
        setCancelAtPeriodEnd(!!entData?.cancelAtPeriodEnd);
        setTrialEndsAt(entData?.currentPeriodEnd ?? null);
        // TASK-331 phase 2: this switch reached the change-plan.ts branch that
        // releases any PRIOR downgrade schedule first (upgrade/lateral while
        // one exists) — sync from the fresh response rather than leaving a
        // stale badge on whatever card used to hold it.
        const scheduledDowngrade = resolveScheduledDowngrade(entData);
        setScheduledPlan(scheduledDowngrade?.plan ?? null);
        setScheduledPlanEffectiveAt(scheduledDowngrade?.effectiveAt ?? null);
      }
      // TASK-327: this local state only ever moved the "Current" pill on
      // THIS page — the shared entitlements object every other screen reads
      // (EditStrategy, Settings, Sidebar, ActivationProgressCard, CreatePost)
      // was left exactly as cached (up to its 2-minute TTL) because nothing
      // here ever told DashboardDataContext to refetch. Invalidate both
      // resources the activation card + strategy lock depend on so a
      // paying, upgraded org sees the unlock on its very next render, not
      // whenever some other screen's TTL happens to expire.
      dashboard.invalidateResource?.('entitlements');
      dashboard.invalidateResource?.('setupStatus');
      // Fix (judge critic, item 8): planIncludesResearch(entData) alone is
      // true for a plain Free org too (free's research_runs_monthly is 1),
      // so this used to promise "Complete your brand and Dathent will
      // research your market" even for a brand that already IS strategy-ready
      // (nothing left to complete) or for a plan switch where the brand is a
      // total unknown here. Only show the activation notice when we can
      // actually see a brand with missing fields — read it from the
      // dashboard's own setupStatus probe (same brand-profile fetch
      // DashboardDataContext already ran) rather than firing a second one.
      const setupStatus = dashboard.setupStatus;
      const knownBrand = setupStatus?.brand === 'present' ? setupStatus.profile : null;
      const showActivationNotice = Boolean(entData)
        && planIncludesResearch(entData)
        && knownBrand !== null
        && missingBrandFields(knownBrand).length > 0;
      setSwitchNotice(resolveSwitchNotice({
        chargedNow: !!resultData.chargedNow,
        amountPaidCents: resultData.amountPaidCents,
        // TASK-331 (SPA follow-up): a $0-settled invoice (a 100%-off coupon,
        // or a credit that fully covers the switch) — distinct from
        // chargedNow (nothing was actually charged) and from noChange
        // (the plan really did change). `invoiceUnknown` needs no field
        // here: it already falls through to the plain fallback message,
        // which makes no amount claim either way.
        settledWithoutCharge: !!resultData.settledWithoutCharge,
        noChange: !!resultData.noChange,
        showActivationNotice,
        trialPreserved: !!resultData.trialPreserved,
        cancellationCleared: !!resultData.cancellationCleared,
        planLabel,
        t,
        formatMoney: (cents, currency) => formatPostingMoney(cents, currency, i18n.language || 'en'),
      }).message);
      setNoticeSeq((s) => s + 1); // MINOR 7: force the focus effect even if the message text repeats.
      finishSwitch();
    } catch (err) {
      if (!switchIsCurrent()) return;
      // TASK-331 (SPA follow-up, staging bug report 2026-09-07): change-plan's
      // machine-readable failure reasons (PAYMENT_METHOD_REQUIRED /
      // PAYMENT_METHOD_UNVERIFIED / SUBSCRIPTION_ENDED /
      // SUBSCRIPTION_NOT_CHANGEABLE — see change-plan.ts) get their own copy +
      // CTA instead of the raw server message. Anything else (a different
      // code, or an unrecognized reason) keeps the pre-existing behaviour:
      // show err.message verbatim per-card.
      const resolvedError = resolveSwitchError({
        code: err?.code,
        reason: err?.details?.reason,
        status: err?.details?.status,
        t,
      });
      if (resolvedError) {
        setSwitchError({ planKey, ...resolvedError });
        if (resolvedError.reason === 'SUBSCRIPTION_ENDED') setSubscriptionEndedDuringSwitch(true);
        // MINOR 1 (round 6): no longer echoing resolvedError.message into the
        // generic per-card `error` too — the page-level switchError banner is
        // now scrolled into view + focused (see focusBanner/switchErrorRef
        // above), so it's already guaranteed visible without a second, inline
        // copy under the Scheduler band. That echo was also the only thing
        // populating `error` for a posting-plan switch failure, so dropping it
        // silences the legacy grid's `featured && error` slot (~L1118) too —
        // exactly one banner per failure on both grids now.
      } else {
        // MINOR 8: a VALIDATION_ERROR whose reason isn't one of the four
        // recognized ones is still a machine-checked failure the server
        // rejected on purpose — its raw err.message is developer/ops text,
        // not customer copy. Anything else (no code — a network/unexpected
        // error) keeps the pre-existing raw-message-when-present fallback.
        setError(resolveGenericSwitchError({ code: err?.code, message: err?.message, t }));
      }
      finishSwitch();
    }
  };

  // TASK-331 (SPA follow-up): "I've paid — refresh" on the requiresAction
  // banner. The org is still on its old plan until the hosted invoice is
  // actually paid and the webhook mirrors the change — re-run the same
  // retrying entitlements refetch handleSwitchPlan uses on a normal switch;
  // if it still doesn't match, leave the banner up rather than guessing.
  const handleRefreshAfterPayment = async () => {
    if (!switchAction || busy) return;
    const operation = acquireBillingOperation('payment-refresh', plansIdentity);
    if (!operation) return;
    const lifecycleIsCurrent = switchAction.lifecycleIsCurrent;
    if (lifecycleIsCurrent && !lifecycleIsCurrent()) {
      releaseBillingOperation(operation);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const entData = await refetchSwitchedPlan(switchAction.planKey, lifecycleIsCurrent);
      if (!operationIsCurrent(operation) || (lifecycleIsCurrent && !lifecycleIsCurrent())) return;
      if (entData?.plan === switchAction.planKey) {
        setCurrentPlanId(resolveCurrentPlanId(entData));
        setBillingStatus(entData?.billingStatus || null);
        setEntPlanName(entData?.planName || null);
        setCancelAtPeriodEnd(!!entData?.cancelAtPeriodEnd);
        setTrialEndsAt(entData?.currentPeriodEnd ?? null);
        dashboard.invalidateResource?.('entitlements');
        dashboard.invalidateResource?.('setupStatus');
        clearPlanIntent();
        setSwitchAction(null);
      }
      // Still not there yet — the payment may still be processing (webhook
      // lag); keep the action banner up so the customer can try again.
    } finally {
      if (operationIsCurrent(operation) && (!lifecycleIsCurrent || lifecycleIsCurrent())) {
        setBusy(false);
        releaseBillingOperation(operation);
      }
    }
  };

  // TASK-331 phase 2 (Subscription Schedules) — undo a deferred downgrade
  // (D4: "undo until then"). Positive, reversible action — no confirm modal,
  // same UX class as Settings.jsx's handleUndoScheduledDowngrade. Silently
  // clears the local scheduled-downgrade state (the card's badge/CTA reverts
  // on its own) and invalidates the shared dashboard resource; no full page
  // reload.
  const handleUndoScheduledDowngrade = async () => {
    if (undoingDowngrade || busy) return;
    const expectedPlan = scheduledPlanForBadge;
    const expectedEffectiveAt = expectedPlan ? scheduledPlanEffectiveAt : null;
    const committedSchedule = committedScheduleRef.current;
    const expectedScheduleIdentity = committedSchedule?.identity;
    if (!expectedPlan || expectedScheduleIdentity !== pendingScheduleIdentity
      || committedSchedule.plan !== expectedPlan || committedSchedule.effectiveAt !== expectedEffectiveAt) return;
    const operation = acquireBillingOperation('undo', plansIdentity);
    if (!operation) return;
    operation.pendingPlan = expectedPlan;
    operation.pendingEffectiveAt = expectedEffectiveAt;
    operation.pendingIdentity = expectedScheduleIdentity;
    const undoIsCurrent = () => operationIsCurrent(operation)
      && committedScheduleRef.current?.identity === operation.pendingIdentity
      && committedScheduleRef.current.plan === operation.pendingPlan
      && committedScheduleRef.current.effectiveAt === operation.pendingEffectiveAt;
    if (!undoIsCurrent()) {
      releaseBillingOperation(operation);
      return;
    }
    setUndoingDowngrade(true);
    setError(null);
    // BLOCKER fix B2 (round 7 review): clear the page-level switchNotice (and
    // any stale switchError from an earlier attempt) up front — otherwise a
    // successful undo below leaves the last switch's "Starter starts on ...
    // Until then you keep Pro" banner on screen, directly contradicting the
    // card that just reverted to "Current".
    setSwitchNotice(null);
    setSwitchError(null);
    setSwitchAction(null); // same trio handleSwitchPlan clears — one banner at a time
    try {
      await api.billing.cancelScheduledDowngrade();
      if (!undoIsCurrent()) return;
      const ent = await api.billing.getEntitlements({ fresh: true });
      if (!undoIsCurrent()) return;
      if (!isRecognizedBillingState(ent)) throw new Error('Invalid billing reconciliation response');
      const nextPlanId = resolveCurrentPlanId(ent);
      const nextScheduled = !ent.cancelAtPeriodEnd ? resolveScheduledDowngrade(ent) : null;
      // Any idle review was authorized against the pre-Undo pending facts.
      // Close it before publishing the reconciled snapshot; the next click
      // takes a fresh review rather than presenting a knowingly stale dialog.
      schedulerReviewRequestRef.current += 1;
      activeReviewRef.current = null;
      reviewReadRef.current = null;
      setSchedulerReview(null);
      setSchedulerReviewLoading(false);
      setSchedulerReviewError(false);
      setSchedulerReviewChanged(false);
      setReviewErrorTarget(null);
      setCurrentPlanId(nextPlanId);
      setBillingStatus(ent.billingStatus || ent.subscriptionStatus || null);
      setEntPlanName(ent.planName || null);
      setPaymentIssue(ent.paymentIssue || null);
      setSocialAccountsEnt(ent.socialAccounts || null);
      setAccessState(ent.accessState);
      setHasSubscriptionHistory(ent.hasSubscriptionHistory === true);
      setCancelAtPeriodEnd(!!ent.cancelAtPeriodEnd);
      setTrialEndsAt(ent.currentPeriodEnd ?? null);
      setScheduledPlan(nextScheduled?.plan ?? null);
      setScheduledPlanEffectiveAt(nextScheduled?.effectiveAt ?? null);
      setBillingScopeAccountId(operation.accountId);
      setBillingReady(true);
      setBillingLoadFailed(false);
      dashboard.invalidateResource?.('entitlements');
      // BLOCKER fix B2: a short, neutral confirmation via the existing
      // success-banner pattern (SwitchBanner tone="success") — otherwise the
      // undo now happens in total silence once the contradicting scheduled-
      // downgrade badge/notice above is gone.
      // A still-present pending pair is authoritative (including a replacement
      // schedule); preserve it and make no claim that Undo removed it.
      if (!nextScheduled) {
        setSwitchNotice(t('plansPage.downgradeUndone', { currentPlan: resolveCurrentPlanLabel(nextPlanId) }));
        setNoticeSeq((s) => s + 1);
      }
    } catch (err) {
      if (!undoIsCurrent()) return;
      // BLOCKER fix B1 (round 7 review): route the failure through the SAME
      // page-level switchError + SwitchBanner every other change-plan-
      // adjacent failure on this page uses. `error` only ever renders inside
      // a slot keyed on attemptedPlanId (L952/L1092/L1232 as of the prior
      // round) — a fresh page load's attemptedPlanId is null and this
      // handler never sets it, so a failed release (500/403) used to render
      // NOTHING at all.
      // Logged (not surfaced) — same shape as Settings.jsx's own
      // handleUndoScheduledDowngrade catch, so ops sees this failure the
      // same way regardless of which screen the undo was clicked from.
      console.error('Billing action failed', { type: 'undoScheduledDowngrade', err });
      setSwitchError({
        planKey: null,
        reason: 'UNDO_DOWNGRADE_FAILED',
        message: t('plansPage.undoDowngradeFailed'),
        ctaLabel: t('plansPage.tryAgain'),
        cta: 'retryUndo',
      });
    } finally {
      if (operationIsCurrent(operation)) {
        setUndoingDowngrade(false);
        releaseBillingOperation(operation);
      }
    }
  };

  return (
    <div className="plans-page">
      <div className="plans-wrap">
        <header className="phead">
          <span className="peyebrow"><IconStar />{t('plansPage.eyebrow')}</span>
          <h1>{t('plansPage.title')}</h1>
          <p className="sub">{t('plansPage.subtitle')}</p>
          <ProductVideoLink />
        </header>

        {/* An unpaid/failed invoice on the CURRENT plan: the right action here
            is finishing that payment (Stripe hosted invoice page — accepts a
            different card), not switching plans. Deep research and full limits
            unlock automatically on the paid webhook. */}
        {paymentIssue && (
          <div
            role="alert"
            style={{
              margin: '0 0 18px', padding: '14px 18px', borderRadius: 12,
              background: 'color-mix(in srgb, var(--gold, #C9A961) 12%, var(--bg-subtle))',
              border: '1px solid color-mix(in srgb, var(--gold, #C9A961) 45%, transparent)',
              fontSize: 13.5, lineHeight: 1.55, color: 'var(--text)',
              display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: '1 1 320px' }}>
              {t('plansPage.paymentIssueBanner', {
                plan: entPlanName || currentPlanId || '',
                defaultValue: "Your {{plan}} plan is picked but the first payment didn't go through. Finish the payment — full capabilities (including your deep market research) unlock automatically.",
              })}
            </span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ flexShrink: 0 }}
              onClick={() => {
                if (paymentIssue.invoiceUrl) window.open(paymentIssue.invoiceUrl, '_blank', 'noopener');
                else navigate('/app/settings?tab=billing');
              }}
            >
              {t('plansPage.paymentIssueCta', { defaultValue: 'Pay now' })}
            </button>
          </div>
        )}

        {/* New-grid only: a legacy subscriber has no card in this grid — say so
            quietly instead of leaving every card un-highlighted. Unreachable
            pre-flip (newGridActive false). */}
        {showLegacyBanner && (
          <div
            role="note"
            style={{
              margin: '0 0 18px', padding: '11px 16px', borderRadius: 12,
              background: 'var(--bg-subtle)', border: '1px solid var(--border)',
              fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)', textAlign: 'center',
            }}
          >
            {t('plansPage.legacyPlanBanner', {
              plan: legacyPlanName,
              defaultValue: "You're on {{plan}} (legacy plan) — it keeps working as-is.",
            })}
          </div>
        )}

        {/* TASK-331 (D5): trialing + a scheduled cancellation on the trial's
            sub (owner picked "cancel" earlier, then came back) — say so
            explicitly at the top of the grid; the switch CTAs below stay
            enabled (D2 clears the cancellation on any switch anyway). */}
        {shouldShowCancelSchedulePick({ newGridActive, isTrialing, cancelAtPeriodEnd }) && (
          <div
            role="note"
            style={{
              margin: '0 0 18px', padding: '11px 16px', borderRadius: 12,
              background: 'var(--bg-subtle)', border: '1px solid var(--border)',
              fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)', textAlign: 'center',
            }}
          >
            {t('plansPage.cancelScheduledPick', { date: formatBillingDate(trialEndsAt) })}
          </div>
        )}

        {/* TASK-331 (SPA follow-up): a switch attempt that needs the customer
            to finish payment (3DS/SCA, a declined card) — nothing changed
            yet, so this is NOT the success banner below. Same gold treatment
            as the paymentIssue banner above (same underlying situation: an
            invoice needs attention).
            BLOCKER fix round 5 (consistency): NOT gated on newGridActive, for
            the identical reason switchError below no longer is — handleSwitchPlan
            (which sets this) is reachable only from the Scheduler band (outside
            newGridActive) and the new-grid AI cards (inside it); the legacy
            grid's featured-card CTA is handleSubscribe -> checkout, never
            handleSwitchPlan. MINOR 5 (round 6): corrected — this used to say
            "legacy-grid AI cards", which was never true. */}
        {switchAction && (
          <SwitchBanner tone="gold" message={switchAction.message} refEl={switchActionRef}>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => {
                  if (switchAction.hostedInvoiceUrl) window.open(switchAction.hostedInvoiceUrl, '_blank', 'noopener');
                }}
              >
                {t('plansPage.completePayment')}
              </button>
              <button type="button" className="btn btn-ghost" disabled={busy || billingOperationBusy} onClick={handleRefreshAfterPayment}>
                {t('plansPage.iHavePaidRefresh')}
              </button>
            </div>
          </SwitchBanner>
        )}

        {/* TASK-331 (SPA follow-up, staging bug report 2026-09-07): a
            change-plan failure with a machine-readable reason this page knows
            how to explain (see lib/planSwitchTrial.js's resolveSwitchError).
            Mutually exclusive with switchAction/switchNotice above/below —
            the catch block that sets this never also sets those. The CTA
            wiring lives here (not in resolveSwitchError, which only decides
            copy + which kind of action applies) since it needs navigate()/
            handleSwitchPlan closures resolveSwitchError can't have.
            BLOCKER (round 5): NOT gated on newGridActive — the Scheduler band
            below is rendered OUTSIDE that ternary (postingPlanActive alone)
            and also routes through handleSwitchPlan, so this is the only
            page-level surface for its failures. Gating it on newGridActive
            silently ate the error for a postingPlanActive-but-legacy-catalog
            org (an admin-unpublished/de-self-serviced Starter+Growth): the
            spinner would blink and setBusy(false) restore the label with
            nothing else visible. This banner belongs to the page, not to the
            grid. MINOR 5 (round 6): corrected — the new-grid AI cards further
            down are the only OTHER producer of handleSwitchPlan failures (the
            legacy grid's featured-card CTA is handleSubscribe -> checkout,
            never handleSwitchPlan); this used to say "legacy-grid AI cards". */}
        {switchError && (
          <SwitchBanner tone="gold" message={switchError.message} refEl={switchErrorRef}>
            {switchError.ctaLabel && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || billingOperationBusy}
                  onClick={() => {
                    if (switchError.cta === 'retry') {
                      openPlanReview(switchError.planKey);
                    } else if (switchError.cta === 'billingSettings') {
                      // Reuse the exact route PaymentIssueNotice's own CTAs
                      // resolve to, so this never drifts from that surface.
                      navigate(paymentIssueLinks(null, null).billingSettings);
                    } else if (switchError.cta === 'retryUndo') {
                      // BLOCKER fix B1 (round 7 review): the undo-a-scheduled-
                      // downgrade failure this page-level banner now also
                      // covers — re-run the same handler rather than
                      // handleSwitchPlan (there is no planKey to switch to).
                      handleUndoScheduledDowngrade();
                    }
                    // 'checkout' has no ctaLabel/button — canSwitchInPlace is
                    // already forced false (see subscriptionEndedDuringSwitch
                    // above), so every card's own CTA routes to checkout.
                  }}
                >
                  {switchError.ctaLabel}
                </button>
              </div>
            )}
          </SwitchBanner>
        )}

        {schedulerReviewError && !schedulerReview && reviewErrorTarget && (
          <div role="alert" className="scheduler-review-open-error">
            <span>{t('plansPage.schedulerReviewRefreshError')}</span>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || schedulerReviewLoading || billingOperationBusy}
              onClick={() => openPlanReview(reviewErrorTarget)}
            >
              {t('plansPage.tryAgain')}
            </button>
          </div>
        )}

        {/* Success notice after an in-place plan switch. BLOCKER fix round 5
            (consistency): NOT gated on newGridActive — same reasoning as
            switchAction/switchError above, this is the only page-level
            surface for a switch that succeeded via the Scheduler band, which
            renders outside that ternary. (Legacy-grid cards go through
            handleSubscribe → checkout, never handleSwitchPlan.) */}
        {switchNotice && (
          <SwitchBanner tone="success" message={switchNotice} refEl={switchNoticeRef} />
        )}

        {/* Track 1 — Scheduler ("Publish only"): a full-width band, not a
            card, so its shape alone tells it apart from the AI tiers below
            (B4/B5 fix — see design brief §Layouts).
            TASK-298 fix round (MAJOR 5): hoisted OUTSIDE both the new and
            legacy grids — it used to live inside the `newGridActive`
            ternary, so an admin unpublishing/de-self-servicing Growth
            (isNewGridActive requires starter+growth; isPostingPlanActive
            only requires the posting row) silently removed the Scheduler's
            only /app/plans purchase path. It now renders whenever
            postingPlanActive is true, in EITHER catalog state. */}
        {publishTrack && (
          <section
            className={`plans-band${currentPlanId === 'posting' ? ' is-current' : ''}${currentPlanId !== 'posting' && scheduledPlanForBadge !== 'posting' && planIntentKey === 'posting' ? ' is-intent' : ''}`}
            data-plan="posting"
          >
            <div className="plans-band-left">
              <div className="plans-band-toprow">
                <span className="plans-band-chip">{t('postingPlan.trackLabel')}</span>
                {currentPlanId === 'posting' && <span className="plans-band-badge">{t('settings.current')}</span>}
              </div>
              <h3>{t('postingPlan.name')}</h3>
              <p className="plans-band-sub">{t('postingPlan.publishTrackSubtitle')}</p>
              <ul className="plans-band-feats feats">
                {[1, 2, 3].map((index) => (
                  <li key={index}><span className="ck"><IconCheck /></span><span>{t(`postingPlan.included${index}`)}</span></li>
                ))}
              </ul>
              <ul className="plans-band-excl" aria-label={t('postingPlan.excludedTitle')}>
                {[1, 2, 3, 4].map((index) => <li key={index}><IconNoAi /> {t(`postingPlan.notIncluded${index}`)}</li>)}
              </ul>
            </div>
            <div className="plans-band-price">
              <span className="big">{publishTrack.line.unitFormatted}</span>
              <span className="unit">{t('postingPlan.unit')}</span>
              <span className="plans-band-total">{publishTrack.liveTotalCopy}</span>
              {/* New subscriptions are billed immediately. Existing subscribers
                  review the switch separately because Stripe determines whether
                  a paid change is immediate or scheduled. */}
              {currentPlanId !== 'posting' && (
                <span className="plans-band-total">
                  {isTrialing
                    ? t('plansPage.switchSchedulerTrialNote', { price: publishTrack.line.unitFormatted })
                    : canSwitchInPlace
                      ? t('plansPage.switchSchedulerPaidNote')
                      : t('postingPlan.noTrialNote')}
                </span>
              )}
              {currentPlanId === 'posting' ? (
                // MINOR 8: its own class instead of cta-current, which hard-sets
                // cursor:default — this CTA is enabled and actionable, never a
                // dead "Current plan" stub, so it must look clickable on hover.
                <button type="button" className="cta cta-quiet" onClick={() => navigate('/app/settings?tab=billing')}>
                  {t('postingPlan.manage')}
                </button>
              ) : scheduledPlanForBadge === 'posting' ? (
                // TASK-331 phase 2: a downgrade to Scheduler is scheduled for
                // a future date — badge instead of a switch CTA, plus an
                // undo. The org's CURRENT plan card (above/elsewhere) keeps
                // its own normal "Current" state; this replaces only the
                // TARGET card's CTA. MINOR 6: scheduledPlanForBadge (not the
                // raw scheduledPlan state) — cancel wins, see its definition.
                <div className="cta-scheduled">
                  <span className="badge-scheduled">{t('plansPage.scheduledBadge', { date: formatBillingDate(scheduledPlanEffectiveAt) })}</span>
                  <button type="button" className="cta-text" onClick={handleUndoScheduledDowngrade} disabled={undoingDowngrade || busy || billingOperationBusy}>
                    {undoingDowngrade ? '…' : t('plansPage.undoDowngrade')}
                  </button>
                </div>
              ) : lapsedHasOpenInvoice ? (
                // TASK-331 (fix #6): live-but-unpaid subscription — settle the
                // invoice/portal, not change-plan or checkout (either would
                // dead-end or act on a subscription mid-lapse).
                <button type="button" className="cta cta-gold" onClick={handleLapsedPayInvoice}>
                  {t('lapsed.payOpenInvoiceGeneric')} <IconArrow />
                </button>
              ) : lapsedPastDueNoInvoice ? (
                // TASK-331 phase-1-fixes round 3 (fix #3): still-live
                // past_due subscription with no open invoice to settle —
                // checkout would hit ALREADY_SUBSCRIBED. Portal is the only
                // renew path Stripe exposes here.
                <button type="button" className="cta cta-gold" onClick={handleLapsedRenewPortal} disabled={portalBusy}>
                  {portalBusy ? t('paymentIssue.openingPortal') : t('lapsed.updateCard')} <IconArrow />
                </button>
              ) : canSwitchInPlace ? (
                // Scheduler's billed quantity can change the total, so its
                // unit price cannot determine upgrade/downgrade wording.
                <button type="button" className="cta cta-gold" onClick={() => openPlanReview('posting')} disabled={busy || billingOperationBusy || schedulerReviewLoading || !schedulerBillingEligible}>
                  {schedulerReviewLoading ? '…' : t('plansPage.schedulerReviewConfirm')} <IconArrow />
                </button>
              ) : (
                <button type="button" className="cta cta-gold" onClick={() => handleSubscribeNew('posting')} disabled={busy || billingOperationBusy || !billingReady || billingLoadFailed}>
                  {billingReady && !billingLoadFailed ? t('postingPlan.landingCta') : t('plansPage.billingUnavailable')} <IconArrow />
                </button>
              )}
              {attemptedPlanId === 'posting' && error && (
                <div className="plans-err" role="alert">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                  <span>{error}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {newGridActive ? (
          <>
            <div id="plans-ai-grid" className={`grid ai-grid ${gridColsClass(aiCards.length)}`}>
              {/* Track 2 — AI content plans divider. Only rendered alongside
                  the band: with no Track 1 to contrast against (postingPlanActive
                  false — see design brief States: "Catalog loading / fetch
                  failed"), a "vs the Scheduler track above it" header is
                  redundant, so it is skipped too and the AI grid stands alone. */}
              {publishTrack && (
                <div className="plans-track-head" data-track-head>
                  <span className="plans-track-title">{t('postingPlan.aiTrackTitle')}</span>
                  <span className="plans-track-sub">{t('postingPlan.aiTrackSubtitle')}</span>
                </div>
              )}

              {aiCards.map((p) => {
                // NEW tariff grid (docs/VYTIAG-tarify-core-video-2026-07-06.md §2):
                // all four cards are self-service/live — unlike the legacy grid
                // there's no "soon"/disabled tier here, so this branch is a
                // simplified version of the legacy featured-card rendering below
                // (that ternary's non-featured branches — soon/contact — never
                // apply to the new grid and are intentionally not reproduced).
                const keys = NEW_PLAN_TEXT_KEYS[p.id] || {};
                const isCurrent = billingScopeCurrent && currentPlanId === p.id;
                const isScheduled = !isCurrent && scheduledPlanForBadge === p.id;
                // TASK-313: ?plan=<key> intent highlight — never on a card the
                // org is already subscribed/scheduled for (state treatment wins).
                const isIntent = !isCurrent && !isScheduled && planIntentKey === p.id;
                const catalogFeatured = !!p.featured;
                const marketingFeatured = catalogFeatured && !hasPlansSubscriber && !isScheduled && !isCurrent;
                const name = tx(keys.name, p.name);
                const tier = tx(keys.tier, p.tier);
                const desc = tx(keys.desc, p.desc);
                const display = getNewPlanDisplay(p.id);
                const socialAddonPricing = socialAddonPricingFor(p.id, name);
                // TASK-298: which wording a live subscriber's switch CTA uses —
                // resolved from priceCents so it can never mislabel an upgrade
                // as a "switch" or vice versa (B4/two-track design brief).
                const switchKind = resolveSwitchLabel({ targetPriceCents: p.priceCents, currentPriceCents: currentPriceCentsForComparison });
                const switchLabel = switchKind === 'upgrade'
                  ? t('plansPage.upgradeTo', { plan: name })
                  : switchKind === 'downgrade'
                    ? t('plansPage.downgradeTo', { plan: name })
                    : t('plansPage.switchPlan');

                return (
                  <div key={p.id} className={`card${marketingFeatured ? ' featured' : ''}${isCurrent ? ' is-current' : ''}${isScheduled ? ' is-scheduled' : ''}${isIntent ? ' is-intent' : ''}`} data-plan={p.id}>
                    {marketingFeatured && <span className="floatpill"><IconStar size={11} />{t('settings.recommended')}</span>}
                    {isCurrent && <span className="floatpill current">{t('settings.current')}</span>}

                    <span className="tnum">{tier}</span>
                    <h3>{name}</h3>
                    <p className="desc">{desc}</p>

                    <div className="price">
                      <div className="row">
                        <span className="big">{display.priceFormatted || p.price}</span>
                        <span className="unit">{tx(keys.priceUnit, p.priceUnit)}</span>
                        {(display.priceWasFormatted || p.priceWas) && (
                          <span className="struck">{display.priceWasFormatted || p.priceWas}</span>
                        )}
                      </div>
                      {(display.fromDb ? display.promoBadge : (p.priceBadge && tx(keys.priceBadge, p.priceBadge))) && (
                        <span className="badge">{display.fromDb ? display.promoBadge : tx(keys.priceBadge, p.priceBadge)}</span>
                      )}
                      {(display.fromDb ? display.promoThen : p.priceThen) && (
                        <span className="then">
                          {display.fromDb
                            ? display.promoThen
                            : tx(keys.priceThen, p.priceThen, { price: display.priceWasFormatted || p.priceWas })}
                          {p.priceTag ? ` · ${tx(keys.priceTag, p.priceTag)}` : ''}
                        </span>
                      )}
                    </div>

                    <SocialAddonProvenanceLine
                      pricing={socialAddonPricing}
                      onOpenSheet={(e) => {
                        addonSheetAnchorRef.current = e.currentTarget;
                        setAddonSheet({ open: true, planId: p.id, planName: name });
                      }}
                    />

                    {/* CTA — subscription-aware. The checkout endpoint acts on any
                        EXISTING subscription before reading the requested plan, so
                        it is only ever called when there is NO live/trialing sub. */}
                    {isCurrent && isTrialing ? (
                      <button type="button" className="cta cta-gold" onClick={reviewTrialActivation} disabled={busy}>
                        {busy ? '…' : t('plansPage.reviewActivation')} <IconArrow />
                      </button>
                    ) : isCurrent ? (
                      <button type="button" className="cta cta-current" disabled>{t('settings.currentPlanBtn')}</button>
                    ) : isScheduled ? (
                      // TASK-331 phase 2: this card is the target of an
                      // already-accepted, deferred downgrade — badge instead
                      // of a switch CTA, plus an undo. The org's current-plan
                      // card keeps its own "Current" state (the isCurrent
                      // branch above), unaffected by this branch. MINOR 6:
                      // scheduledPlanForBadge (not the raw scheduledPlan
                      // state) — cancel wins, see its definition above.
                      // MINOR 5 (round 7 review): checked deliberately ahead
                      // of lapsedHasOpenInvoice/lapsedPastDueNoInvoice below
                      // — an accepted, still-active downgrade schedule can
                      // only exist on a currently live, paying subscription
                      // (scheduling one requires it), so a card that reaches
                      // this branch is never ALSO lapsed/past-due in
                      // practice; even so, the reversible undo action here is
                      // the safer default over routing into a lapsed-payment
                      // CTA that would just fight this schedule.
                      <div className="cta-scheduled">
                        <span className="badge-scheduled">{t('plansPage.scheduledBadge', { date: formatBillingDate(scheduledPlanEffectiveAt) })}</span>
                        <button type="button" className="cta-text" onClick={handleUndoScheduledDowngrade} disabled={undoingDowngrade || busy || billingOperationBusy}>
                          {undoingDowngrade ? '…' : t('plansPage.undoDowngrade')}
                        </button>
                      </div>
                    ) : lapsedHasOpenInvoice ? (
                      /* TASK-331 (fix #6): live-but-unpaid subscription — settle
                         the invoice/portal, not change-plan or checkout. */
                      <button type="button" className="cta cta-gold" onClick={handleLapsedPayInvoice}>
                        {t('lapsed.payOpenInvoiceGeneric')} <IconArrow />
                      </button>
                    ) : lapsedPastDueNoInvoice ? (
                      /* TASK-331 phase-1-fixes round 3 (fix #3): still-live
                         past_due subscription with no open invoice to settle
                         — checkout would hit ALREADY_SUBSCRIBED. Portal is
                         the only renew path Stripe exposes here. */
                      <button type="button" className="cta cta-gold" onClick={handleLapsedRenewPortal} disabled={portalBusy}>
                        {portalBusy ? t('paymentIssue.openingPortal') : t('lapsed.updateCard')} <IconArrow />
                      </button>
                    ) : canSwitchInPlace ? (
                      /* TASK-331 (D1/D5): live paying subscriber (new-grid or
                         legacy plan) OR a trialing org — both switch in place
                         via the change-plan endpoint. Trialing gets its own
                         "Switch to <Plan>" wording (no charge now, so an
                         upgrade/downgrade framing doesn't apply) in a
                         secondary (cta-quiet) style, distinct from the gold
                         "you will be charged" switch CTA a live sub gets. */
                      <button
                        type="button"
                        className={`cta ${isTrialing ? 'cta-quiet' : 'cta-gold'}`}
                        onClick={() => openPlanReview(p.id)}
                        disabled={busy || billingOperationBusy}
                      >
                        {busy && attemptedPlanId === p.id ? '…' : (isTrialing ? t('plansPage.switchTrialCta', { plan: name }) : switchLabel)} <IconArrow />
                      </button>
                    ) : (
                      <button type="button" className="cta cta-gold" onClick={() => handleSubscribeNew(p.id)} disabled={busy || billingOperationBusy}>
                        {tx(keys.cta, p.cta)} <IconArrow />
                      </button>
                    )}

                    {attemptedPlanId === p.id && error && (
                      <div className="plans-err" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                        <span>{error}</span>
                      </div>
                    )}

                    {isCurrent && isTrialing && (
                      <p className="plans-trialpay" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8, opacity: 0.85 }}>
                        {t('plansPage.trialPayNote', "You're on a free trial. Pay now to start your subscription early.")}{' '}
                        {card?.last4
                          ? t('plansPage.willChargeCard', { card: `•••• ${card.last4}`, defaultValue: "We'll charge {{card}}." })
                          : t('plansPage.willChargeSaved', "We'll charge your saved card.")}{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/app/settings?tab=billing')}
                          style={{ background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                        >
                          {t('plansPage.manageCard', 'Manage card')}
                        </button>
                      </p>
                    )}

                    {/* TASK-331 (D5): a non-current card's switch CTA while
                        trialing — the trial isn't ending, it's just now
                        billing this card's price once it does. Skipped when
                        the trial end date isn't known yet rather than
                        rendering an incomplete sentence. */}
                    {!isCurrent && isTrialing && trialEndsAt && (
                      <p className="plans-trialpay" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8, opacity: 0.85 }}>
                        {t('plansPage.switchTrialNote', {
                          price: display.priceFormatted || p.price,
                          date: formatBillingDate(trialEndsAt),
                        })}
                      </p>
                    )}

                    <ul className="feats">
                      <li><span className="ck"><IconCheck /></span><span>{t('postingPlan.higherTierContrast')}</span></li>
                      {p.feats.map((f, i) => (
                        <li key={i}>
                          <span className="ck"><IconCheck /></span>
                          <span>{keys.feats?.[i] ? t(`settings.${keys.feats[i]}`) : f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="plans-more-toggle"
              aria-expanded={showAllPlans}
              aria-controls="plans-ai-grid"
              onClick={() => setShowAllPlans((v) => !v)}
            >
              {showAllPlans ? t('plansPage.showFewerPlans') : t('plansPage.seeAllPlans')}
            </button>
          </>
        ) : (
        <div className="grid">
          {PLAN_DEFS.filter((p) => !p.contact).map((p) => {
            const keys = PLAN_TEXT_KEYS[p.id] || {};
            const isCurrent = currentPlanId === p.id;
            const featured = !!p.featured;
            const name = tx(keys.name, p.name);
            const tier = tx(keys.tier, p.tier);
            const desc = tx(keys.desc, p.desc);
            // DB-backed display catalog (docs/TARIFF-REWORK-BRIEF.md §3/§4):
            // falls back to this exact PLAN_DEFS entry's hardcoded price/promo
            // text whenever the server field is null/missing or the fetch
            // hasn't resolved yet — so first paint and any offline state are
            // byte-identical to the pre-Phase-4 hardcoded render.
            const display = getPlanDisplay(p.id);

            return (
              <div key={p.id} className={`card ${featured ? 'featured' : ''} ${p.soon ? 'soon' : ''}`}>
                {featured && !isCurrent && <span className="floatpill"><IconStar size={11} />{t('settings.recommended')}</span>}
                {featured && isCurrent && <span className="floatpill current">{t('settings.current')}</span>}
                {p.soon && <span className="soonpill">{t('settings.comingSoon')}</span>}

                <span className="tnum">{tier}</span>
                <h3>{name}</h3>
                <p className="desc">{desc}</p>

                {/* price */}
                <div className="price">
                  {featured ? (
                    <>
                      <div className="row">
                        <span className="big">{display.priceFormatted || p.price}</span>
                        <span className="unit">{tx(keys.priceUnit, p.priceUnit)}</span>
                        {(display.priceWasFormatted || p.priceWas) && (
                          <span className="struck">{display.priceWasFormatted || p.priceWas}</span>
                        )}
                      </div>
                      {/* promoBadge/promoThen: an admin-edited DB row's raw text wins
                          (it isn't localized per-language, same as the DB display_name/
                          description already aren't); otherwise use the translated
                          hardcoded copy exactly as before. */}
                      {(display.fromDb ? display.promoBadge : (p.priceBadge && tx(keys.priceBadge, p.priceBadge))) && (
                        <span className="badge">{display.fromDb ? display.promoBadge : tx(keys.priceBadge, p.priceBadge)}</span>
                      )}
                      {(display.fromDb ? display.promoThen : p.priceThen) && (
                        <span className="then">
                          {display.fromDb
                            ? display.promoThen
                            // planPilotPriceThen interpolates {{price}} — the "was"/full
                            // price the founding discount reverts to (numbers are
                            // language-neutral; only the surrounding words are translated).
                            : tx(keys.priceThen, p.priceThen, { price: display.priceWasFormatted || p.priceWas })}
                          {p.priceTag ? ` · ${tx(keys.priceTag, p.priceTag)}` : ''}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="soonprice">{tx(keys.soonPrice, p.soonPrice)}</span>
                      <span className="then">{tx(keys.soonNote, p.soonNote)}</span>
                    </>
                  )}
                </div>

                {/* cta */}
                {featured && isCurrent && isTrialing ? (
                  <button type="button" className="cta cta-gold" onClick={reviewTrialActivation} disabled={busy}>
                    {busy ? '…' : t('plansPage.reviewActivation')} <IconArrow />
                  </button>
                ) : featured && isCurrent ? (
                  <button type="button" className="cta cta-current" disabled>{t('settings.currentPlanBtn')}</button>
                ) : featured ? (
                  <button type="button" className="cta cta-gold" onClick={handleSubscribe} disabled={busy || billingOperationBusy}>
                    {tx(keys.cta, p.cta)} <IconArrow />
                  </button>
                ) : (
                  <button type="button" className="cta cta-disabled" disabled>{t('settings.comingSoon')}</button>
                )}

                {featured && error && (
                  <div className="plans-err" role="alert">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                    <span>{error}</span>
                  </div>
                )}

                {/* Trial → pay-now note: what card we'll charge + a manage link. */}
                {featured && isCurrent && isTrialing && (
                  <p className="plans-trialpay" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8, opacity: 0.85 }}>
                    {t('plansPage.trialPayNote', "You're on a free trial. Pay now to start your subscription early.")}{' '}
                    {card?.last4
                      ? t('plansPage.willChargeCard', { card: `•••• ${card.last4}`, defaultValue: "We'll charge {{card}}." })
                      : t('plansPage.willChargeSaved', "We'll charge your saved card.")}{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/app/settings?tab=billing')}
                      style={{ background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                    >
                      {t('plansPage.manageCard', 'Manage card')}
                    </button>
                  </p>
                )}

                <ul className="feats">
                  <li><span className="ck"><IconCheck /></span><span>{t('postingPlan.higherTierContrast')}</span></li>
                  {p.feats.map((f, i) => (
                    <li key={i}>
                      <span className="ck"><IconCheck /></span>
                      <span>{keys.feats?.[i] ? t(`settings.${keys.feats[i]}`) : f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        )}

        {(() => {
          const ent = PLAN_DEFS.find((p) => p.contact);
          if (!ent) return null;
          const ek = PLAN_TEXT_KEYS[ent.id] || {};
          return (
            <div className="ent-strip">
              <div className="ent-l">
                <span className="ent-tag">{ent.tier} · {tx(ek.name, ent.name)}</span>
                <p className="ent-desc">{tx(ek.desc, ent.desc)}</p>
              </div>
              <button type="button" className="ent-cta" onClick={() => setEntOpen(true)}>
                {tx(ek.cta, ent.cta)} <IconArrow />
              </button>
            </div>
          );
        })()}

        <footer className="pfoot">
          <span className="reassure">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
            {t('plansPage.secure')}<span className="dot" />{t('plansPage.cancelAnytime')}
          </span>
          {showManagedPaymentsNote && (
            <p className="plans-mp-note" style={{ margin: 0, maxWidth: '64ch', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-subtle)' }}>
              {t('checkout.managedPaymentsNote')}
            </p>
          )}
          <button type="button" onClick={() => navigate('/app/settings?tab=billing')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
            {t('plansPage.backToBilling')}
          </button>
        </footer>
      </div>

      <ContactOptionsModal open={entOpen} onClose={() => setEntOpen(false)} source="app-plans-enterprise" />

      {/* TASK-329: opens from a card's "N connected" segment (ONLY when that
          card's socialAddonPricing.extra > 0). This page only ever renders
          for an org with entitlements already resolved (reached via
          Settings' "Change plan" or an upgrade CTA), so — unlike
          PlanSelectScreen — Settings is a safe "Connect another" target. */}
      <ConnectedAccountsSheet
        open={addonSheet.open}
        pricing={addonSheet.open ? socialAddonPricingFor(addonSheet.planId, addonSheet.planName) : null}
        anchorRef={addonSheetAnchorRef}
        onClose={() => setAddonSheet((s) => ({ ...s, open: false }))}
        onConnectAnother={() => { setAddonSheet((s) => ({ ...s, open: false })); navigate('/app/settings?tab=accounts'); }}
        onDisconnected={() => {
          api.billing.getEntitlements()
            .then((ent) => setSocialAccountsEnt(ent?.socialAccounts || ent?.data?.socialAccounts || null))
            .catch(() => {});
        }}
      />
      <PlanSwitchReviewDialog
        review={schedulerReview}
        changed={schedulerReviewChanged}
        error={schedulerReviewError}
        confirming={busy || schedulerReviewLoading || billingOperationBusy}
        confirmDisabled={!schedulerReviewCurrent || billingOperationBusy}
        onConfirm={confirmSchedulerSwitch}
        onCancel={closeSchedulerReview}
      />
    </div>
  );
}
